// LT-471 — Tabbed edit modal for the Contact cockpit.
//
// Replaces the standalone EditContactIntelligenceModal (LT-212), which
// exposed only the post-call classification fields and left identity
// (name / email / phone / title) un-editable from the cockpit page.
//
// Two tabs:
//   - Details: first_name, last_name, email, phone, title — single PATCH.
//   - Classification: current_temperature, current_disposition with the
//     LT-212 two-step confirm because the analyzer overwrites these on
//     the next call.

import React, { useEffect, useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';

import { TEMPERATURE_CONFIG } from '../../components/intelligence/TemperaturePill';
import {
  DISPOSITION_GROUPS,
  DISPOSITION_MAP,
} from '../../components/intelligence/DispositionBadge';
import { Button, IconButton, Input, Select, Tabs } from '../../ui';
import type { Contact } from '../../types/contact';

export interface ContactDetailsPatch {
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  phone?: string | null;
  title?: string | null;
}

export interface ContactIntelligencePatch {
  current_temperature?: string | null;
  current_disposition?: string | null;
}

type EditTab = 'details' | 'classification';

interface EditContactModalProps {
  isOpen: boolean;
  contact: Contact;
  saving: boolean;
  onClose: () => void;
  onSaveDetails: (patch: ContactDetailsPatch) => void;
  onSaveClassification: (patch: ContactIntelligencePatch) => void;
}

const TEMPERATURE_OPTIONS = ['hot', 'warm', 'lukewarm', 'cold', 'dead'];
const UNSET_VALUE = '__UNSET__';

const TAB_ITEMS = [
  { value: 'details', label: 'Details' },
  { value: 'classification', label: 'Classification' },
] as const;

function nullableTrim(v: string): string | null {
  const t = v.trim();
  return t === '' ? null : t;
}

function valuesEqual(a: string | null | undefined, b: string | null | undefined): boolean {
  return (a ?? null) === (b ?? null);
}

const EditContactModal: React.FC<EditContactModalProps> = ({
  isOpen,
  contact,
  saving,
  onClose,
  onSaveDetails,
  onSaveClassification,
}) => {
  const [tab, setTab] = useState<EditTab>('details');

  // Details fields.
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [title, setTitle] = useState('');
  const [detailsError, setDetailsError] = useState<string | null>(null);

  // Classification fields (mirrors LT-212 two-step confirm).
  const [temperature, setTemperature] = useState('');
  const [disposition, setDisposition] = useState('');
  const [classificationConfirmArmed, setClassificationConfirmArmed] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setTab('details');
    setFirstName(contact.first_name ?? '');
    setLastName(contact.last_name ?? '');
    setEmail(contact.email ?? '');
    setPhone(contact.phone ?? '');
    setTitle(contact.title ?? '');
    setDetailsError(null);
    setTemperature(contact.current_temperature ?? '');
    setDisposition(contact.current_disposition ?? '');
    setClassificationConfirmArmed(false);
  }, [isOpen, contact]);

  if (!isOpen) return null;

  const detailsDirty =
    !valuesEqual(nullableTrim(firstName), contact.first_name) ||
    !valuesEqual(nullableTrim(lastName), contact.last_name) ||
    !valuesEqual(nullableTrim(email), contact.email) ||
    !valuesEqual(nullableTrim(phone), contact.phone) ||
    !valuesEqual(nullableTrim(title), contact.title);

  const classificationDirty =
    (temperature || null) !== (contact.current_temperature || null) ||
    (disposition || null) !== (contact.current_disposition || null);

  const handleSaveDetails = () => {
    if (!detailsDirty || saving) return;
    // Trimmed empty + already empty → no field change. Trimmed non-empty
    // values go through as-is; null clears the field server-side.
    const patch: ContactDetailsPatch = {};
    const nextFirst = nullableTrim(firstName);
    if (!valuesEqual(nextFirst, contact.first_name)) patch.first_name = nextFirst;
    const nextLast = nullableTrim(lastName);
    if (!valuesEqual(nextLast, contact.last_name)) patch.last_name = nextLast;
    const nextEmail = nullableTrim(email);
    if (!valuesEqual(nextEmail, contact.email)) patch.email = nextEmail;
    const nextPhone = nullableTrim(phone);
    if (!valuesEqual(nextPhone, contact.phone)) patch.phone = nextPhone;
    const nextTitle = nullableTrim(title);
    if (!valuesEqual(nextTitle, contact.title)) patch.title = nextTitle;

    // Soft client-side checks — backend's EmailStr is the source of truth.
    if (nextEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(nextEmail)) {
      setDetailsError('Email looks invalid — use the format name@example.com.');
      return;
    }
    if (nextPhone && !/^\+\d{6,}$/.test(nextPhone)) {
      setDetailsError('Phone must be in E.164 format, e.g. +61491570006.');
      return;
    }
    setDetailsError(null);
    onSaveDetails(patch);
  };

  const handleSaveClassification = () => {
    if (!classificationDirty || saving) return;
    if (!classificationConfirmArmed) {
      setClassificationConfirmArmed(true);
      return;
    }
    const patch: ContactIntelligencePatch = {};
    if ((temperature || null) !== (contact.current_temperature || null)) {
      patch.current_temperature = temperature || null;
    }
    if ((disposition || null) !== (contact.current_disposition || null)) {
      patch.current_disposition = disposition || null;
    }
    onSaveClassification(patch);
  };

  const onTemperatureChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const raw = e.target.value;
    setTemperature(raw === UNSET_VALUE ? '' : raw);
    setClassificationConfirmArmed(false);
  };

  const onDispositionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const raw = e.target.value;
    setDisposition(raw === UNSET_VALUE ? '' : raw);
    setClassificationConfirmArmed(false);
  };

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="lt471-title"
    >
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        aria-hidden="true"
        onClick={saving ? undefined : onClose}
      />
      <div className="relative z-10 w-full max-w-lg rounded-ds-xl bg-surface-1 border border-white/[0.08] shadow-ds-e3">
        <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-white/[0.08]">
          <h2 id="lt471-title" className="text-lg font-semibold text-slate-50">Edit contact</h2>
          <IconButton
            intent="ghost"
            size="sm"
            onClick={onClose}
            disabled={saving}
            aria-label="Close"
          >
            <XMarkIcon className="w-4 h-4" />
          </IconButton>
        </div>

        <div className="px-4 sm:px-6 pt-4">
          <Tabs
            items={TAB_ITEMS.map((t) => ({ value: t.value, label: t.label }))}
            value={tab}
            onChange={(v) => setTab(v as EditTab)}
            aria-label="Edit contact sections"
          />
        </div>

        {tab === 'details' ? (
          <>
            <div className="px-4 sm:px-6 py-4 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Input
                  label="First name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  disabled={saving}
                  autoComplete="given-name"
                />
                <Input
                  label="Last name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  disabled={saving}
                  autoComplete="family-name"
                />
              </div>
              <Input
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={saving}
                autoComplete="email"
                placeholder="name@example.com"
              />
              <Input
                label="Phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                disabled={saving}
                autoComplete="tel"
                placeholder="+61491570006"
                hint="E.164 format — include country code with leading +."
              />
              <Input
                label="Title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                disabled={saving}
                placeholder="e.g. Head of Sales"
              />
              {detailsError ? (
                <p className="text-[12px] text-danger-text" role="alert">{detailsError}</p>
              ) : null}
            </div>
            <div className="flex items-center justify-end gap-2 px-4 sm:px-6 py-4 border-t border-white/[0.08]">
              <Button intent="ghost" onClick={onClose} disabled={saving}>
                Cancel
              </Button>
              <Button
                intent="primary"
                onClick={handleSaveDetails}
                disabled={!detailsDirty || saving}
              >
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className="px-4 sm:px-6 py-4 space-y-4">
              <p className="text-xs text-slate-400">
                Manual override for the values the OpenAI post-call analyzer
                sets. Subsequent calls will overwrite these again — there is no
                sticky flag.
              </p>

              <Select
                id="lt471-temperature"
                label="Temperature"
                value={temperature || UNSET_VALUE}
                onChange={onTemperatureChange}
                disabled={saving}
              >
                <option value={UNSET_VALUE}>— unset —</option>
                {TEMPERATURE_OPTIONS.map((t) => (
                  <option key={t} value={t}>
                    {TEMPERATURE_CONFIG[t]?.label ?? t}
                  </option>
                ))}
              </Select>

              <Select
                id="lt471-disposition"
                label="Disposition"
                value={disposition || UNSET_VALUE}
                onChange={onDispositionChange}
                disabled={saving}
              >
                <option value={UNSET_VALUE}>— unset —</option>
                {Object.entries(DISPOSITION_GROUPS).map(([groupKey, group]) => (
                  <optgroup key={groupKey} label={group.label}>
                    {group.dispositions.map((d) => (
                      <option key={d} value={d}>
                        {DISPOSITION_MAP[d]?.label ?? d}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </Select>
            </div>
            <div className="flex items-center justify-end gap-2 px-4 sm:px-6 py-4 border-t border-white/[0.08]">
              <Button intent="ghost" onClick={onClose} disabled={saving}>
                Cancel
              </Button>
              <Button
                intent={classificationConfirmArmed ? 'danger' : 'primary'}
                onClick={handleSaveClassification}
                disabled={!classificationDirty || saving}
              >
                {saving
                  ? 'Saving…'
                  : classificationConfirmArmed
                  ? 'Confirm save'
                  : 'Save'}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default EditContactModal;
